<template>
  <div class="weekly-report">
    <div class="toolbar">
      <date-range-picker
          v-model="form"
          :disabled="loading"
          clearable
          start-placeholder="开始时间"
          end-placeholder="结束时间"
          style="width: 350px"
      ></date-range-picker>
      <el-button type="primary" :loading="loading" @click="downloadReport">生成报表</el-button>
    </div>

    <el-alert
        type="info"
        :closable="false"
        show-icon
        title="固定口径：Fantasy Jigsaw / Android / US；除“盲盒进入数u”外，其它指标只统计模块为图库的图片。"
        style="margin: 12px 0"
    ></el-alert>

    <el-table v-if="preview" :data="[preview.summary]" style="margin-top: 12px">
      <el-table-column label="时间" prop="period" width="110"></el-table-column>
      <el-table-column label="日更" prop="daily_count" width="90"></el-table-column>
      <el-table-column label="AI图占比" width="110">
        <template #default="scope">{{ percent(scope.row.ai_ratio) }}</template>
      </el-table-column>
      <el-table-column label="展示u" prop="impression_u_avg" width="110"></el-table-column>
      <el-table-column label="进入数u" prop="enter_u_avg" width="110"></el-table-column>
      <el-table-column label="进入率u" width="110">
        <template #default="scope">{{ percent(scope.row.enter_rate_u) }}</template>
      </el-table-column>
      <el-table-column label="完成率u" width="110">
        <template #default="scope">{{ percent(scope.row.finish_rate_u) }}</template>
      </el-table-column>
      <el-table-column label="盲盒进入数u" prop="daily_enter_u_avg" width="130"></el-table-column>
      <el-table-column label="AI图6%以下" prop="ai_rate_lt_6" width="120"></el-table-column>
    </el-table>
  </div>
</template>

<script>
import axios from "ts-axios-new";
import * as XLSX from "xlsx";
import {saveAs} from "file-saver";
import {ElMessage} from "element-plus";
import DateRangePicker from "../base/DateRangePicker.vue";
import {timestampToDate} from "../libs/utils";
import {
  SUMMARY_COLUMNS,
  buildSummaryRows,
  getSummaryPercentageCells,
} from "../libs/weeklyReportColumns.mjs";

function setRangeFormat(sheet, range, format) {
  range.forEach(cell => {
    if (sheet[cell]) {
      sheet[cell].z = format;
    }
  });
}

export default {
  name: "WeeklyReport",
  components: {DateRangePicker},
  data() {
    const now = Date.now();
    return {
      loading: false,
      preview: null,
      form: {
        start: timestampToDate(now - 7 * 24 * 3600 * 1000),
        end: timestampToDate(now - 24 * 3600 * 1000),
      },
    };
  },
  methods: {
    percent(value) {
      return `${((value || 0) * 100).toFixed(2)}%`;
    },
    async downloadReport() {
      if (!this.form.start || !this.form.end) {
        ElMessage.warning("请选择时间范围");
        return;
      }
      this.loading = true;
      try {
        const res = await axios.get("/cms/v1/report/weekly", {params: this.form});
        const report = res.data.data;
        this.preview = report;
        this.saveWorkbook(report);
        ElMessage.success("报表已生成");
      } catch (e) {
        const message =
            e?.response?.data?.message ||
            e?.response?.data?.status?.message ||
            e?.message ||
            "生成报表失败";
        ElMessage.error(message);
      } finally {
        this.loading = false;
      }
    },
    saveWorkbook(report) {
      const wb = XLSX.utils.book_new();
      const summarySheet = this.buildSummarySheet(report.summary);
      const categoryASheet = this.buildCategorySheet(report.summary.period, report.categoryA, report.categoryATotal);
      const categoryBSheet = this.buildCategorySheet(report.summary.period, report.categoryB, report.categoryBTotal);

      XLSX.utils.book_append_sheet(wb, summarySheet, "周数据汇总");
      XLSX.utils.book_append_sheet(wb, categoryASheet, "分类数据-A");
      XLSX.utils.book_append_sheet(wb, categoryBSheet, "分类数据-B");

      const wbout = XLSX.write(wb, {bookType: "xlsx", bookSST: false, type: "array"});
      const blob = new Blob([wbout], {type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"});
      saveAs(blob, `Jigsaw周报-${report.meta.start}-${report.meta.end}.xlsx`);
    },
    buildSummarySheet(summary) {
      const rows = [
        SUMMARY_COLUMNS.map(column => column.label),
        ...buildSummaryRows(summary),
      ];
      const sheet = XLSX.utils.aoa_to_sheet(rows);
      sheet["!cols"] = SUMMARY_COLUMNS.map(column => ({wch: column.width || 12}));
      setRangeFormat(sheet, getSummaryPercentageCells(2), "0.00%");
      return sheet;
    },
    buildCategorySheet(period, rows, total) {
      const totalRow = this.toCategoryRow(total || this.fallbackCategoryTotal(rows));
      const data = [
        [`${period}uv`, "", "", "", ""],
        ["分类", "平均值项:展示(u)", "平均值项:进入数(u)", "计数项:ID", "进入率"],
        ...rows.map(this.toCategoryRow),
        totalRow,
      ];
      const sheet = XLSX.utils.aoa_to_sheet(data);
      sheet["!merges"] = [{s: {r: 0, c: 0}, e: {r: 0, c: 4}}];
      sheet["!cols"] = [{wch: 16}, {wch: 18}, {wch: 18}, {wch: 12}, {wch: 12}];
      [...rows, totalRow].forEach((_, index) => {
        const rowNumber = index + 3;
        setRangeFormat(sheet, [`E${rowNumber}`], "0.00%");
      });
      return sheet;
    },
    toCategoryRow(row) {
      return [
        row.category,
        row.impression_u_avg,
        row.enter_u_avg,
        row.count,
        row.enter_rate,
      ];
    },
    fallbackCategoryTotal(rows) {
      const count = rows.reduce((sum, row) => sum + (row.count || 0), 0);
      if (!count) {
        return {category: "总计", impression_u_avg: 0, enter_u_avg: 0, count: 0, enter_rate: 0};
      }
      const weightedAverage = key => {
        const total = rows.reduce((sum, row) => sum + (Number(row[key]) || 0) * (row.count || 0), 0);
        return Math.round(total / count * 10000) / 10000;
      };
      return {
        category: "总计",
        impression_u_avg: weightedAverage("impression_u_avg"),
        enter_u_avg: weightedAverage("enter_u_avg"),
        count,
        enter_rate: weightedAverage("enter_rate"),
      };
    },
  },
};
</script>

<style scoped>
.weekly-report {
  padding: 4px;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
